import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  EInterfaceContent,
  modalsListT,
  ModalStatusT,
  EServerStatus,
  IInnerLoaders,
} from "./appTypes";
import { getProjectThunk } from "../ui/uiThunks";
import { matchThunkStatus } from "../../../utils/functions/matchThunkStatus";
import {
  deleteHistoryItemThunk,
  getHistoryListThunk,
} from "../history/historyThunks";

export interface initialAppState {
  isShowLoader: boolean;
  innerLoaders: IInnerLoaders;
  modalsList: modalsListT;
  serverStatus: EServerStatus;
  wsConnected: boolean;
  interfaceContent: EInterfaceContent;
  pageSize: number;
  isShowOverlayHint: boolean;
  isToolbarReady: boolean;
}

const initialState: initialAppState = {
  isShowLoader: false,
  innerLoaders: {
    isShowHistoryLoader: false,
    isShowEnvironmentLoader: false,
  },
  serverStatus: EServerStatus.Pending,
  modalsList: {
    saveConfig: {
      isShow: false,
    },
    renderLoader: {
      isShow: false,
    },
    loader: {
      isShow: false,
    },
    glbLoader: {
      isShow: false,
    },
    renderPreview: {
      isShow: false,
    },
    fullScreenPreview: {
      isShow: false,
    },
    pleaseNote: {
      isShow: false,
    },
    serverOffline: {
      isShow: false,
    },
    createFolderModal: {
      isShow: false,
    },
    createTemplateModal: {
      isShow: false,
    },
    createPresetModal: {
      isShow: false,
    },
    sceneHint: { isShow: false },
    resetScene: { isShow: false },
  },
  wsConnected: false,
  interfaceContent: EInterfaceContent.Tabs,
  pageSize: 20,
  isShowOverlayHint: sessionStorage.getItem("isShowOverlayHint") !== "false",
  isToolbarReady: false,
};

const historyActions = [getHistoryListThunk, deleteHistoryItemThunk];
const environmentActions = [getProjectThunk];

const appSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    showLoader(state) {
      state.isShowLoader = true;
    },
    hideLoader(state) {
      state.isShowLoader = false;
    },
    changeShowModal(state, action) {
      const { nameModal, stateModal, otherParams } = action.payload;
      const paramsModals: ModalStatusT = {
        isShow: stateModal,
        otherParams: otherParams,
      };

      state.modalsList = {
        ...state.modalsList,
        [nameModal]: paramsModals,
      };
    },
    setWsConnected(state, action) {
      const { status } = action.payload;
      state.wsConnected = status;
    },
    setServerStatus(
      state,
      action: PayloadAction<{ serverStatus: EServerStatus }>
    ) {
      const { serverStatus } = action.payload;
      state.serverStatus = serverStatus;
    },
    setInterfaceContent(
      state,
      action: PayloadAction<{ interfaceContent: EInterfaceContent }>
    ) {
      const { interfaceContent } = action.payload;
      state.interfaceContent = interfaceContent;
    },
    toggleSceneHint(state) {
      const currentState = state.modalsList.sceneHint.isShow;
      state.modalsList.sceneHint.isShow = !currentState;
    },
    disableSceneHint(state, action: PayloadAction<{ isShow: boolean }>) {
      state.modalsList.sceneHint.isShow = action.payload.isShow;
    },
    setShowOverlayHint(state, action: PayloadAction<boolean>) {
      state.isShowOverlayHint = action.payload;
      sessionStorage.setItem("isShowOverlayHint", String(action.payload));
    },
    setToolbarReady(state, action: PayloadAction<boolean>) {
      state.isToolbarReady = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addMatcher(matchThunkStatus(historyActions, "pending"), state => {
      state.innerLoaders.isShowHistoryLoader = true;
    });
    builder.addMatcher(matchThunkStatus(historyActions, "rejected"), state => {
      state.innerLoaders.isShowHistoryLoader = false;
    });
    builder.addMatcher(matchThunkStatus(historyActions, "fulfilled"), state => {
      state.innerLoaders.isShowHistoryLoader = false;
    });
    builder.addMatcher(
      matchThunkStatus(environmentActions, "pending"),
      state => {
        state.innerLoaders.isShowEnvironmentLoader = true;
      }
    );
    builder.addMatcher(
      matchThunkStatus(environmentActions, "rejected"),
      state => {
        state.innerLoaders.isShowEnvironmentLoader = false;
      }
    );
    builder.addMatcher(
      matchThunkStatus(environmentActions, "fulfilled"),
      state => {
        state.innerLoaders.isShowEnvironmentLoader = false;
      }
    );
  },
});

export const {
  showLoader,
  hideLoader,
  changeShowModal,
  setWsConnected,
  setServerStatus,
  setInterfaceContent,
  toggleSceneHint,
  setShowOverlayHint,
  setToolbarReady,
  disableSceneHint,
} = appSlice.actions;
export const appReducer = appSlice.reducer;
